/**
 * The three things worth recording that no single screen owns: that somebody
 * signed in, that something crashed, and that a control looks broken.
 *
 * Armed once from `main.js` after sign-in. Everything else in the app emits
 * its own events at the point the thing happens — `concierge_message` where a
 * message is sent, `trip_created` inside `lib/trips.js`. These three have no
 * such place, which is the only reason they are collected here.
 *
 * ── Sign-in is the one that matters most ──────────────────────────────────
 *
 * `/insights` builds "Active (7d)" from `analytics_users.lastSeen`, and every
 * event refreshes that — so ANY event would do. But sign-in is the one that
 * fires for an advisor who opens the app, looks at their trips and closes it
 * again, which is most sessions and the exact behaviour retention is made of.
 * Without it, a quiet week reads as nobody being there.
 *
 * Once per page load, not once per `onUser` call: Firebase re-reports the
 * user on a token refresh, and counting those would turn one advisor's
 * afternoon into a dozen sign-ins.
 *
 * ── What is deliberately not here ─────────────────────────────────────────
 *
 * Legacy's "analytics v2" block also polls a `page_view` every three seconds
 * and emits `field_edit` on every committed field and `search` on every
 * search. Each event is TWO Firestore writes, so that poll alone is on the
 * order of 2,400 writes an hour per signed-in advisor. `lib/usage.js` has the
 * test that ruled them out.
 */

import { track } from './usage.js'

/** Four clicks inside this box counts as one. Legacy's rule (original.html). */
export const RAGE_RADIUS_PX = 24

/** …and within this long. */
export const RAGE_WINDOW_MS = 1500

/** How many clicks make it rage rather than a double-click. */
export const RAGE_CLICKS = 4

/**
 * Longest error message kept. `sanitizeMeta` caps it again at
 * `MAX_META_LENGTH`; this is here so the intent is visible at the call site.
 */
export const MAX_ERROR_CHARS = 200

/**
 * Where a click landed, for the rage detector. Exported for its test, which
 * would otherwise have to fake pointer events to reach it.
 *
 * A run continues when a click is close in BOTH space and time. Space alone
 * would count somebody using the same button all afternoon; time alone would
 * count fast work anywhere on the screen.
 *
 * @param {{x: number, y: number, at: number, count: number}|null} run
 * @param {{x: number, y: number, at: number}} click
 * @returns {{x: number, y: number, at: number, count: number}}
 */
export function extendRun(run, click) {
  if (!run) return { ...click, count: 1 }
  const near =
    Math.abs(click.x - run.x) <= RAGE_RADIUS_PX && Math.abs(click.y - run.y) <= RAGE_RADIUS_PX
  const soon = click.at - run.at <= RAGE_WINDOW_MS
  if (!near || !soon) return { ...click, count: 1 }
  /* The anchor stays where the run STARTED, so a slow drift across the page
     at four clicks a second is not one long rage — each click has to be near
     the first, not merely near the one before it. */
  return { x: run.x, y: run.y, at: click.at, count: run.count + 1 }
}

/**
 * What a control that looks broken is called, for the event.
 *
 * The element's own text is never recorded — a button can be labelled with a
 * client's name ("Email the Harts"). A tag and a `data-` attribute name are
 * enough to find it in the source, which is the only thing this is for.
 *
 * @param {any} target
 * @returns {string}
 */
export function describeTarget(target) {
  if (!target || !target.tagName) return 'unknown'
  const tag = String(target.tagName).toLowerCase()
  const marker = target.attributes
    ? [...target.attributes].map((a) => a.name).find((n) => n.startsWith('data-'))
    : ''
  return marker ? `${tag}[${marker}]` : tag
}

/** Armed once per page load, whatever `onUser` does afterwards. */
let armed = false

/**
 * Start recording. Safe to call repeatedly; only the first call does anything.
 *
 * @param {{doc?: any, emit?: (type: string, meta?: any) => void, now?: () => number}} [deps]
 * @returns {() => void} teardown, for tests
 */
export function startUsageSignals(deps = {}) {
  const emit = deps.emit || track
  const doc = deps.doc || (typeof globalThis !== 'undefined' ? globalThis.document : null)
  const now = deps.now || (() => Date.now())
  const host = doc && doc.defaultView ? doc.defaultView : globalThis

  if (armed) return () => {}
  armed = true

  emit('login')

  /** @type {{x: number, y: number, at: number, count: number}|null} */
  let run = null

  /** @param {any} e */
  function onClick(e) {
    run = extendRun(run, {
      x: Number(e.clientX) || 0,
      y: Number(e.clientY) || 0,
      at: now(),
    })
    if (run.count < RAGE_CLICKS) return
    emit('rage_click', { at: describeTarget(e.target) })
    /* Reset, so ten angry clicks are one event and not seven. */
    run = null
  }

  /** @param {any} e */
  function onError(e) {
    const message = e && (e.message || (e.error && e.error.message))
    emit('js_error', {
      msg: String(message || 'unknown').slice(0, MAX_ERROR_CHARS),
    })
  }

  /** @param {any} e */
  function onRejection(e) {
    const reason = e && e.reason
    const message = reason && reason.message ? reason.message : reason
    emit('js_error', {
      msg: String(message || 'unhandled rejection').slice(0, MAX_ERROR_CHARS),
    })
  }

  if (doc && doc.addEventListener) doc.addEventListener('click', onClick, true)
  if (host && host.addEventListener) {
    host.addEventListener('error', onError)
    host.addEventListener('unhandledrejection', onRejection)
  }

  return () => {
    armed = false
    if (doc && doc.removeEventListener) doc.removeEventListener('click', onClick, true)
    if (host && host.removeEventListener) {
      host.removeEventListener('error', onError)
      host.removeEventListener('unhandledrejection', onRejection)
    }
  }
}
